import type {
  UseFormRegister,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from "react-hook-form";
import type { Geofence } from "../../../../../../../store/admin/geofencesStore";
import type { ThemeClasses } from "../../types";

interface CircularCoordinatesFormProps {
  register: UseFormRegister<Geofence>;
  watch: UseFormWatch<Geofence>;
  trigger: UseFormTrigger<Geofence>;
  errors: FieldErrors<Geofence>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  defaultLatitude: number;
  defaultLongitude: number;
  compact?: boolean;
}

interface CircularRadiusFormProps {
  register: UseFormRegister<Geofence>;
  watch: UseFormWatch<Geofence>;
  trigger: UseFormTrigger<Geofence>;
  errors: FieldErrors<Geofence>;
  themeClasses: ThemeClasses;
  compact?: boolean;
}

const radiusError = (errors: FieldErrors<Geofence>) =>
  errors.coordinates &&
  !Array.isArray(errors.coordinates) &&
  (errors.coordinates as any).radius;

export function CircularRadiusForm({
  register,
  watch,
  trigger,
  errors,
  themeClasses,
  compact = false,
}: CircularRadiusFormProps) {
  const slider = (
    <input
      type="range"
      min={50}
      max={2000}
      {...register("coordinates.radius", {
        required: true,
        min: 50,
        max: 2000,
        onChange: () => {
          trigger("coordinates.radius");
        },
      })}
      className="w-full h-2 bg-subtle rounded-lg appearance-none cursor-pointer accent-primary-500"
    />
  );

  return (
    <div>
      <div className="w-full flex items-center gap-3">
        <label
          className={`block text-sm font-medium ${themeClasses.text} ${compact ? "" : "mb-1"}`}
        >
          Radio (metros):
        </label>
        <span className={`flex text-sm font-medium ${themeClasses.text}`}>
          {watch("coordinates.radius")} m
        </span>
      </div>
      {slider}
      <div className="w-full flex justify-between text-xs text-content-faint mt-1">
        <span>50 m</span>
        <span>2000 m</span>
      </div>
      {radiusError(errors) && (
        <span className="text-error text-xs">Radio entre 50 y 2000</span>
      )}
    </div>
  );
}

export function CircularCoordinatesForm({
  register,
  watch,
  trigger,
  errors,
  themeClasses,
  defaultLatitude,
  defaultLongitude,
  compact = false,
}: CircularCoordinatesFormProps) {
  const inputClassName = `w-full min-h-10 text-xs px-3 py-2 pr-16 bg-app border ${themeClasses.cardBorder} rounded-lg ${
    themeClasses.text
  } focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const labelClassName = `block text-sm font-medium ${themeClasses.text} ${compact ? "" : "mb-1"}`;
  const fieldGap = compact ? "gap-1.5" : "gap-2";

  const coordinateFields = (
    <>
      <div className={`flex flex-col ${fieldGap}`}>
        <label className={labelClassName}>Latitud</label>
        <input
          type="number"
          step="0.0001"
          value={Number(watch("coordinates.latitude") || defaultLatitude).toFixed(4)}
          {...register("coordinates.latitude", {
            required: true,
          })}
          className={inputClassName}
          placeholder="Ej: 19.4326"
        />
        {errors.coordinates &&
          !Array.isArray(errors.coordinates) &&
          (errors.coordinates as any).latitude && (
            <span className="text-error text-xs">Campo obligatorio</span>
          )}
      </div>
      <div className={`flex flex-col ${fieldGap}`}>
        <label className={labelClassName}>Longitud</label>
        <input
          type="number"
          step="0.0001"
          value={Number(watch("coordinates.longitude") || defaultLongitude).toFixed(4)}
          {...register("coordinates.longitude", {
            required: true,
          })}
          className={inputClassName}
          placeholder="Ej: -99.1332"
        />
        {errors.coordinates &&
          !Array.isArray(errors.coordinates) &&
          (errors.coordinates as any).longitude && (
            <span className="text-error text-xs">Campo obligatorio</span>
          )}
      </div>
    </>
  );

  if (compact) return coordinateFields;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{coordinateFields}</div>
      <CircularRadiusForm
        register={register}
        watch={watch}
        trigger={trigger}
        errors={errors}
        themeClasses={themeClasses}
      />
    </>
  );
}
